import * as Sentry from '@sentry/react';
import { ClientProjectEntity, ClientSiteEntity, ClientRemoteServiceEntity, ClientSiteDraft } from '@edgebox/api-rest-client';
import { DynamicReference, Uuid } from '@edgebox/data-definition-kit';
import React from 'react';
import { ApiComponent, IApiComponentState } from '../../common';
import { INavigateProp, withNavigate } from '../RouterHelper';
import { InternalRole } from '@edgebox/data-definitions';
import { SiteApplicationType, SiteEnvironmentType, SiteStatus } from '@edgebox/sync-core-data-definitions';
import moment from 'moment';

async function loadRemoteScript(id: string, src: string): Promise<void> {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

interface IExternalProps {
  scope: 'content' | 'configuration';
  syncCore: ClientRemoteServiceEntity;
  site?: ClientSiteEntity;
  project?: ClientProjectEntity;
  embed: EmbedPath;
  width?: string;
  height?: string;
  options?: any;
  onCount?: (count: number, entityType: string) => void;
}

interface IProps extends IExternalProps, INavigateProp {}

interface IState extends IApiComponentState {
  loaded?: boolean;
  backendJwt?: string;
  imageJwt?: string;
  iframeId?: string;
  previousOptions?: any;
}

const EMBED_DOMAIN = process.env.REACT_APP_EMBED_DOMAIN || 'embed.content-sync.io';

/** The class that shows a frame over the whole window while it has a dialog open (`src/styles.scss`). */
const IFRAME_MODAL_CLASS = 'iframe-modal';
const CLOUD_EMBED_BASE_URL = `//${EMBED_DOMAIN}`;

export type EmbedPath =
  | 'brand-presence'
  | 'brand-presence/taxonomies'
  | 'brand-presence/monitoring'
  | `brand-presence/monitoring/${string}/runs`
  | `brand-presence/monitoring/${string}/run`
  | 'brand-presence/settings'
  | 'brand-presence/settings/taxonomies'
  | 'brand-presence/settings/monitoring'
  | 'brand-presence/settings/crawling'
  | `brand-presence/settings/monitoring/${string}/runs`
  | `brand-presence/settings/monitoring/${string}/run`
  | 'brand-presence/taxonomy-management'
  | 'brand-presence/monitoring-prompts'
  | 'brand-presence/crawling-settings'
  | 'brand-presence/onboarding'
  | 'brand-presence/content-inventory'
  | `brand-presence/content-inventory/${string}`
  | `brand-presence/content-inventory/${string}/${string}`
  | 'brand-presence/analytics'
  | 'brand-presence/analytics/content-health'
  | `brand-presence/analytics/content-health/${string}`
  | 'brand-presence/analytics/ai-visibility'
  | `brand-presence/analytics/ai-visibility/${string}`
  | 'brand-presence/analytics/ai-answers'
  | `brand-presence/analytics/ai-answers/${string}`
  | 'brand-presence/jobs'
  | 'brand-presence/jobs/groups'
  | 'brand-presence/jobs/tasks'
  | `brand-presence/jobs/groups/${string}`
  | `brand-presence/jobs/tasks/${string}`
  | 'pull-dashboard'
  | 'syndication-dashboard'
  | 'site-registered'
  | 'formula'
  | 'box/content-count/customer'
  | 'box/onboarding/configuration/push'
  | 'box/onboarding/configuration/pull'
  | 'box/onboarding/syndication/push'
  | 'box/onboarding/syndication/pull'
  | 'box/onboarding/migration/push'
  | 'site-settings'
  | 'notifications'
  | 'ai/provider'
  | 'ai/model'
  | 'ai/prompt-template'
  | 'ai/prompt-execution';

class EmbedComponent extends ApiComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props, {
      iframeId: `embed--${props.embed.replace(/[^a-zA-Z0-9-_]/g, '-')}--${Date.now()}--${Math.round(Math.random() * 1_000_000_000_000)}`,
    });
  }

  get isAiEmbed() {
    return this.props.embed.startsWith('ai/');
  }

  get isBrandPresenceEmbed() {
    return this.props.embed === 'brand-presence' || this.props.embed.startsWith('brand-presence/');
  }

  get isFormulaEmbed() {
    return this.props.embed === 'formula';
  }

  get requiresSite() {
    return !this.isAiEmbed && !this.isFormulaEmbed && !this.isBrandPresenceEmbed;
  }

  protected normalizeDomainToHttpsUrl(value: string): { baseUrl: string; domain: string } | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    let candidate = trimmed;
    if (!/^https?:\/\//i.test(candidate)) {
      candidate = `https://${candidate}`;
    }

    try {
      const parsed = new URL(candidate);
      if (!/^https?:$/i.test(parsed.protocol)) {
        return undefined;
      }

      const domain = parsed.hostname.toLowerCase();
      if (!domain || domain.includes(' ')) {
        return undefined;
      }

      return {
        domain,
        baseUrl: `${parsed.protocol}//${domain}`,
      };
    } catch {
      return undefined;
    }
  }

  protected sendIframeResponse(callbackId: string | undefined, response: unknown) {
    if (!callbackId || !this.iframe?.iFrameResizer) {
      return;
    }

    this.iframe.iFrameResizer.sendMessage({
      type: 'response',
      callbackId,
      response,
    });
  }

  protected async handleResolveBrandPresenceOnboardingSite(message: any) {
    const callbackId = typeof message?.callbackId === 'string' ? message.callbackId : undefined;

    try {
      const domain = typeof message?.domain === 'string' ? message.domain : '';
      const normalized = this.normalizeDomainToHttpsUrl(domain);
      if (!normalized) {
        this.sendIframeResponse(callbackId, { error: 'Please enter a valid domain name.' });
        return;
      }

      if (!this.props.project?.id || !this.props.syncCore?.id) {
        this.sendIframeResponse(callbackId, { error: 'Project or Sync Core context is missing.' });
        return;
      }

      const existing = await this.api.billing.sites.search(
        {
          page: 0,
          itemsPerPage: 1,
        },
        {
          projectId: this.props.project.id,
        },
        {
          baseUrl: normalized.baseUrl,
          includeInactive: true,
        }
      );

      let site = existing.items[0];

      if (!site) {
        const draft = new ClientSiteDraft({
          name: normalized.domain,
          baseUrl: normalized.baseUrl,
          status: SiteStatus.Active,
          appType: SiteApplicationType.Website,
          appVersion: '0.0.0',
          appModuleVersion: '0.0.0',
          environmentType: this.props.project.type || SiteEnvironmentType.Production,
          domains: [normalized.domain],
          lastActivity: moment(),
          uuid: '' as Uuid,
          customer: new DynamicReference(this.props.project.customer.getId(), this.api.billing.customers.item),
          contract: new DynamicReference(this.props.project.contract.getId(), this.api.billing.contracts.item),
          project: new DynamicReference(this.props.project.id, this.api.syndication.projects.item),
          syncCore: new DynamicReference(this.props.syncCore.id, this.api.syndication.remoteServices.item),
        });

        site = await this.api.billing.sites.create(draft);
      }

      this.sendIframeResponse(callbackId, {
        site: {
          id: String(site.id),
          uuid: String(site.uuid),
          baseUrl: site.baseUrl,
          name: site.name,
        },
      });
    } catch (error) {
      this.sendIframeResponse(callbackId, {
        error: (error as Error)?.message || 'Failed to resolve onboarding site.',
      });
    }
  }

  async load() {
    await loadRemoteScript('embed-iframe-resizer', `${CLOUD_EMBED_BASE_URL}/iframeResizer.js`);

    const isAiEmbed = this.props.embed.startsWith('ai/');
    if (this.requiresSite && !this.props.site) {
      throw new Error(`Embed '${this.props.embed}' requires a site.`);
    }

    if (this.isBrandPresenceEmbed && !this.props.project?.id) {
      throw new Error(`Brand Presence embed '${this.props.embed}' requires a project.`);
    }

    if (isAiEmbed && !this.props.project && !this.api.currentUser?.internalRoles?.includes(InternalRole.Admin)) {
      throw new Error(`AI embed '${this.props.embed}' requires a project.`);
    }

    const backendJwtPromise = this.requiresSite
      ? this.api.syndication.remoteServices.getEmbedToken(this.props.syncCore.id, this.props.scope, this.props.site!.id)
      : this.api.syndication.remoteServices.getEmbedTokenV2(this.props.syncCore.id, { projectId: this.props.project?.id });

    const imageJwtPromise = this.props.site
      ? this.api.syndication.remoteServices.getEmbedToken(this.props.syncCore.id, 'file-download', this.props.site.id)
      : undefined;

    const [{ jwt: backendJwt }, imageJwtResponse] = await Promise.all([backendJwtPromise, imageJwtPromise]);

    return {
      loaded: true,
      backendJwt,
      imageJwt: imageJwtResponse?.jwt,
    };
  }

  protected iframeRef: any;
  protected iframe: any = undefined;

  // A Brand Presence screen takes its content's height, so the page's scroll area (#page-content)
  // scrolls it rather than the frame itself. The frame is told which band of it is in view, in its
  // own coordinates: how far its top edge has scrolled above the area (`top`) and how far the band
  // reaches down from there (`height`), so the screen can keep its navigation in view and fit what
  // it lays over the part in view. The area is reported again when it scrolls, when the window
  // resizes, and when the area itself changes size, as it does when the menu above it opens.
  protected hostScrollArea?: HTMLElement;
  protected hostScrollAreaObserver?: ResizeObserver;
  protected hostViewportTop?: number;
  protected hostViewportHeight?: number;
  protected hostViewportRequest?: number;
  protected hostScrollBeforeDialog?: number;

  /**
   * The visible area's top and bottom edges in the frame's own coordinates: the scroll area's, or for a
   * frame shown over the whole window for a dialog, the window's. Negative above the frame's top edge.
   */
  protected visibleAreaInFrame(frame: HTMLIFrameElement): { top: number; bottom: number } {
    const area = frame.classList.contains(IFRAME_MODAL_CLASS) ? undefined : this.hostScrollArea;
    const frameTop = frame.getBoundingClientRect().top;
    const visibleTop = area ? area.getBoundingClientRect().top : 0;
    const visibleBottom = area ? visibleTop + area.clientHeight : window.innerHeight;
    return { top: visibleTop - frameTop, bottom: visibleBottom - frameTop };
  }

  protected reportHostViewport = () => {
    if (this.hostViewportRequest !== undefined) {
      return;
    }
    this.hostViewportRequest = window.requestAnimationFrame(() => {
      this.hostViewportRequest = undefined;
      const frame: HTMLIFrameElement | null = this.iframeRef;
      if (!this.__isMounted || !this.iframe || !frame) {
        return;
      }
      // The visible band of the frame, in the frame's own coordinates: from how far its top edge is
      // scrolled above the area's top down to the area's bottom. A frame shown over the whole window
      // for a dialog is visible from its top edge down to the window's bottom.
      const visibleArea = this.visibleAreaInFrame(frame);
      const top = Math.max(0, Math.round(visibleArea.top));
      const height = Math.max(0, Math.round(visibleArea.bottom) - top);
      if (top === this.hostViewportTop && height === this.hostViewportHeight) {
        return;
      }
      this.hostViewportTop = top;
      this.hostViewportHeight = height;
      this.iframe.iFrameResizer.sendMessage({ type: 'host-viewport', top, height });
    });
  };

  protected watchHostViewport() {
    this.unwatchHostViewport();
    this.hostScrollArea = window.document.getElementById('page-content') ?? undefined;
    this.hostScrollArea?.addEventListener('scroll', this.reportHostViewport, { passive: true });
    window.addEventListener('resize', this.reportHostViewport);
    if (this.hostScrollArea && typeof ResizeObserver !== 'undefined') {
      this.hostScrollAreaObserver = new ResizeObserver(this.reportHostViewport);
      this.hostScrollAreaObserver.observe(this.hostScrollArea);
    }
    this.hostViewportTop = undefined;
    this.hostViewportHeight = undefined;
    this.reportHostViewport();
  }

  protected unwatchHostViewport() {
    this.hostScrollArea?.removeEventListener('scroll', this.reportHostViewport);
    this.hostScrollArea = undefined;
    this.hostScrollAreaObserver?.disconnect();
    this.hostScrollAreaObserver = undefined;
    window.removeEventListener('resize', this.reportHostViewport);
    if (this.hostViewportRequest !== undefined) {
      window.cancelAnimationFrame(this.hostViewportRequest);
      this.hostViewportRequest = undefined;
    }
  }

  /**
   * A Brand Presence screen asks for a part of its frame to be brought into view (`scroll-into-view`),
   * in the frame's own coordinates: the element the keyboard moved to, with the bars the screen pins
   * over the top of the visible area above it. The scroll area moves by the least that shows the part,
   * its start first where the part is taller than the area. A frame shown over the whole window for a
   * dialog is not in the scroll area, so nothing moves for it.
   */
  protected scrollFramePartIntoView(part: { top: unknown; bottom: unknown }) {
    const frame: HTMLIFrameElement | null = this.iframeRef;
    const area = this.hostScrollArea;
    if (
      !frame ||
      !area ||
      frame.classList.contains(IFRAME_MODAL_CLASS) ||
      typeof part.top !== 'number' ||
      typeof part.bottom !== 'number'
    ) {
      return;
    }
    const visibleArea = this.visibleAreaInFrame(frame);
    const scrollBy =
      part.top < visibleArea.top
        ? part.top - visibleArea.top
        : part.bottom > visibleArea.bottom
          ? Math.min(part.bottom - visibleArea.bottom, part.top - visibleArea.top)
          : 0;
    if (scrollBy) {
      area.scrollTop += scrollBy;
    }
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();
    this.unwatchHostViewport();
  }

  setTextInputRef(element: any) {
    this.iframeRef = element;

    this.mounted();
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (
      this.props.options
        ? !prevProps.options || JSON.stringify(this.props.options) !== JSON.stringify(prevProps.options)
        : !!prevProps.options
    ) {
      if (this.iframe) {
        this.iframe.iFrameResizer.sendMessage({
          type: 'update-options',
          options: {
            ...(this.props.options || {}),
            imagePreviewJwt: this.state.imageJwt,
          },
        });
      }
    }
  }

  mounted() {
    if (!this.state.loaded || !this.iframeRef || !this.__isMounted) {
      return;
    }

    const { backendJwt } = this.state;

    const config: any = {
      featureFlags: {
        'is-backend': 1,
      },
      syncCoreDomain: this.props.syncCore.baseUrl.replace(/^.*\/\/([^/]+).*$/, '$1'),
      baseUrl: this.props.syncCore.baseUrl,
    };

    if (!this.requiresSite) {
      // V2 token is project-scoped and must be used as-is (no site-based exchange).
      config.jwt = backendJwt;
      config.backendJwt = backendJwt;
    } else {
      // Legacy behavior: exchange backendJwt for a site-scoped jwt inside the Embed iframe.
      config.siteUuid = this.props.site!.uuid;
      config.backendJwt = backendJwt;
    }

    const init = () => {
      if (!(window as any).iFrameResize) {
        setTimeout(init, 250);
        return;
      }
      (window as any).iFrameResize(
        {
          //log: true,
          checkOrigin: false,
          autoResize: true,
          onInit: (newIframe: any) => {
            this.iframe = newIframe;
            const options = {
              ...(this.props.options || {}),
              imagePreviewJwt: this.state.imageJwt,
            } as any;

            if (this.isBrandPresenceEmbed) {
              options.projectUuid = this.props.project?.uuid;
              delete options.projectId;
            }

            this.iframe.iFrameResizer.sendMessage({
              type: 'config',
              config,
            });
            this.iframe.iFrameResizer.sendMessage({
              type: 'options',
              options,
            });

            if (this.isBrandPresenceEmbed) {
              this.watchHostViewport();
            }
          },
          onMessage: ({ message }: any) => {
            if (!this.__isMounted) {
              return;
            }

            // Need a fresh access token.
            if (message.type === 'reload') {
              window.location.reload();
            } else if (message.type === 'scroll-to-top') {
              // Doesn't work in IE but that's alright.
              const scrollContainer = window.document.getElementById('page-content');
              if (scrollContainer) {
                scrollContainer.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: 'smooth',
                });
              }
            } else if (message.type === 'scroll-into-view') {
              this.scrollFramePartIntoView(message);
            } else if (message.type === 'modal-open' || message.type === 'modal-close') {
              if (this.iframeRef) {
                const container: HTMLIFrameElement = this.iframeRef;

                const open = message.type === 'modal-open';
                // A frame the page scrolls leaves the page while it covers the window, so the page
                // shrinks and loses its place; the place is kept and given back when the frame returns.
                if (open && this.hostScrollArea) {
                  this.hostScrollBeforeDialog = this.hostScrollArea.scrollTop;
                }
                container.className = open ? IFRAME_MODAL_CLASS : '';
                if (!open && this.hostScrollArea && this.hostScrollBeforeDialog !== undefined) {
                  this.hostScrollArea.scrollTop = this.hostScrollBeforeDialog;
                  this.hostScrollBeforeDialog = undefined;
                }
              }
              // The frame leaves or rejoins the page without a scroll; its position is reported anew.
              if (this.hostScrollArea) {
                this.reportHostViewport();
              }
            } else if (message.type === 'update-query') {
              const query = Object.entries<string>(message.query)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');
              this.props.navigate(`?${query}`);
            } else if (message.type === 'update-path') {
              if (
                this.isBrandPresenceEmbed &&
                this.props.project?.id &&
                typeof message.path === 'string' &&
                (message.path.startsWith('/brand-presence/') ||
                  message.path.startsWith('/settings/taxonomies') ||
                  message.path.startsWith('/settings/monitoring-prompts') ||
                  message.path.startsWith('/settings/crawling'))
              ) {
                const normalizedPath = message.path.startsWith('/') ? message.path : `/${message.path}`;
                const nextPath = `/projects/${this.props.project.id}${normalizedPath}`;
                if (window.location.pathname !== nextPath) {
                  // The bare Brand Presence path is a handover the embed always resolves forward,
                  // never a destination: replace it so it cannot sit underneath the landed page and
                  // swallow the Back button. Every other path is pushed, as before. One trailing
                  // slash spells the same bare path and the embed collapses it the same way, so a
                  // strict comparison would push there instead and reopen the trap.
                  const isHandover = window.location.pathname.replace(/\/$/, '') === `/projects/${this.props.project.id}/brand-presence`;
                  // The handover carries the query string across, as the embed does on its own side
                  // of the seam: the area's screens read their state from it, and the path posted
                  // back has no query on it, so dropping it would strand the address bar behind the
                  // iframe. Only a path that stays inside the area may carry it -- an older embed can
                  // still post a `/settings/...` path, and this query belongs to Brand Presence.
                  const carriesQuery = isHandover && normalizedPath.startsWith('/brand-presence/');
                  this.props.navigate(carriesQuery ? `${nextPath}${window.location.search}` : nextPath, { replace: isHandover });
                }
              }
            } else if (message.type === 'resolve-brand-presence-onboarding-site') {
              void this.handleResolveBrandPresenceOnboardingSite(message);
            } else if (message.type === 'count') {
              this.props.onCount?.(message.count, message.entityType);
            } else {
              // A type this App does not handle, one a newer Embed sends or a request no screen here
              // answers, is left alone the way the Embed leaves a host's, and reported, so it is seen.
              Sentry.captureMessage(`Unknown frame message ${String(message.type)}`, 'warning');
            }
          },
        },
        `#${this.state.iframeId}`
      );
    };

    init();
  }

  render() {
    const { embed, width, height } = this.props;
    const url = `${CLOUD_EMBED_BASE_URL}/${embed}`;

    if (!this.state.loaded) {
      return this.renderRequest();
    }

    return (
      <iframe
        key={this.props.site?.id ?? this.props.project?.id ?? this.state.iframeId}
        title={this.state.iframeId}
        ref={(element) => this.setTextInputRef(element)}
        id={this.state.iframeId}
        src={url}
        frameBorder="0"
        style={{
          width: '1px',
          minWidth: width ? width : '100%',
          minHeight: height ? height : '200px',
          height: height ? height : undefined,
        }}
        allow="fullscreen"
      >
        The page could not be loaded as your browser does not support it.
      </iframe>
    );
  }
}

export const Embed = withNavigate<IExternalProps>(EmbedComponent);
